import React from 'react';
import { UserContext } from '../../context/UserContext';
import { CompanyContext } from '../../context/CompanyContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { GroupContext } from '../../context/GroupContext';
import { Box, Grid, Theme, useTheme } from '@mui/material';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import {
  BodyText,
  CaptionText,
  Heading,
  MinorHeading
} from '../../components/shared/typography/PrimaryTypography';
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { Resource, ResourceMetadata } from '@esg/esg-global-types';
import { getResources } from '../../graphql/services/resource.service';
import { GCP_RESOURCE_UPLOAD_BUCKET } from '../../env';
import PrimarySkeleton from '../../components/shared/loading/PrimarySkeleton';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import moment from 'moment';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import { ReactComponent as DownloadIcon } from '../../assets/icons/Download.svg';
import {
  createResourceMetadata,
  getResourceMetadata,
  updateResourceMetadata
} from '../../graphql/services/resource_metadata.service';
import {
  CreateResourceMetadataClientData,
  UpdateResourceMetadataClientData
} from '@esg/syntiro-api-core';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { downloadFileFromSignedUrl } from '../../lib/files/file';
import GhostButton from '../../components/shared/button/GhostButton';
import HlsVideoViewer from '../../components/shared/media/HlsVideoViewer';
import PdfViewer from '../../components/shared/media/PdfViewer';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import BorderContainer from '../../components/shared/container/BorderContainer';
import ColorTag from '../../components/shared/decoration/ColorTag';

/**
 * View for displaying the details of a single Resource Centre resource.
 * @returns {JSX.Element}
 */
const ResourceCentreDetails = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [resource, setResource] = React.useState<Resource | null>(null);
  const [resourceMetadata, setResourceMetadata] = React.useState<ResourceMetadata | null>(null);
  const [loadingResourceDownload, setLoadingResourceDownload] = React.useState<boolean>(false);
  const [access, setAccess] = React.useState<ViewAccess>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const theme: Theme = useTheme();

  const resource_categories: Array<string> = resource?.categories ?? [];
  const resource_tags: Array<string> = resource?.tags ?? [];

  const handleNavigate = (url: string): void => {
    navigate(url);
  };

  // Calc to show size in B, KB, MB or GB
  const getFileSizeDisplay = (bytes: string): string => {
    const num_bytes = Number(bytes);
    if (isNaN(num_bytes)) return '';
    const size_units: Array<string> = ['B', 'KB', 'MB', 'GB'];

    let display_value: number = num_bytes;
    let unit_index = 0;
    while (display_value > 1024) {
      display_value /= 1024;
      unit_index++;
    }
    return `${display_value.toFixed(2).replace(/\.?0+$/, '')} ${size_units[unit_index]}`;
  };

  // Display message for resource download count rounded to nearest 1000 (floor).
  const getDownloadsDisplay = (downloads: number): string => {
    if (downloads < 1000) return downloads.toString();
    return `${Math.floor(downloads / 1000) * 1000}+`;
  };

  const handleResourceDownload = async (): Promise<void> => {
    setLoadingResourceDownload(true);
    try {
      if (resource && group && company && user) {
        await downloadFileFromSignedUrl(GCP_RESOURCE_UPLOAD_BUCKET, resource.uri);
        if (resourceMetadata) {
          const update_resource_metadata_client_data: UpdateResourceMetadataClientData = {
            id: resourceMetadata.id,
            download_count: resourceMetadata.download_count + 1,
            master_list_resource: resource
          };
          const updated_resource_metadata: ResourceMetadata | null | undefined =
            await updateResourceMetadata(
              { group_id: group.id, company_id: company.id },
              update_resource_metadata_client_data
            );
          if (updated_resource_metadata) setResourceMetadata(updated_resource_metadata);
        } else {
          const create_resource_metadata_client_data: CreateResourceMetadataClientData = {
            download_count: 1,
            master_list_resource: resource
          };
          const created_resource_metadata: ResourceMetadata | null | undefined =
            await createResourceMetadata(
              { group_id: group.id, company_id: company.id },
              create_resource_metadata_client_data
            );
          if (created_resource_metadata) setResourceMetadata(created_resource_metadata);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ResourceCard failed on an unknown error while calling handleResourceDownload.',
          {
            resource: resource
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to download resource. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingResourceDownload(false);
    }
  };

  React.useEffect(() => {
    let resource_found = false;
    if (location.state && location.state.resource) {
      setResource(location.state.resource);
      setResourceMetadata(
        location.state.resource_metadata ? location.state.resource_metadata : null
      );
      setSearchParams({ resource: location.state.resource.id });
    } else {
      (async () => {
        if (group && company) {
          try {
            const resources: Array<Resource> = await getResources();
            const resources_metadata: Array<ResourceMetadata> = await getResourceMetadata({
              group_id: group.id,
              company_id: company.id
            });
            if (resources.length === 0) {
              handleNavigate('/resources/explore');
            }
            for (let i = 0; i < resources.length; i++) {
              if (resources[i].id === searchParams.get('resource')) {
                setResource(resources[i]);
                resource_found = true;
              }
            }
            for (let i = 0; i < resources_metadata.length; i++) {
              if (resources_metadata[i].master_list_resource.id === searchParams.get('resource')) {
                setResourceMetadata(resources_metadata[i]);
              }
            }
            if (!resource_found) {
              handleNavigate('/resources/explore');
            }
          } catch (err: unknown) {
            const tracking_id: string = generateUuidV4();
            log(
              'error',
              new MetadataError(
                err instanceof Error
                  ? err.message
                  : 'Error: ResourceCentreDetails failed on an unknown error while initialising.',
                {
                  group: group,
                  company: company
                },
                tracking_id
              )
            );
            setFeedbackData({
              message: `Unable to initialise Resource Centre Details. Tracking ID: ${tracking_id}`,
              state: true,
              type: 'error'
            });
            return;
          }
        }
      })();
    }
  }, [group, company]);

  React.useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'resource_centre.dashboard')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box sx={{ boxSizing: 'border-box' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'start' }}>
        <GhostButton
          startIcon={<KeyboardArrowLeftIcon />}
          dark
          outlined
          onClick={() => handleNavigate('/resources/explore')}
          sx={{ minWidth: '250px', height: '50px' }}
        >
          Resource Centre
        </GhostButton>
      </Box>
      <Grid container spacing={4} sx={{ my: 8, justifyContent: 'center' }}>
        <Grid size={5} sx={{ textAlign: 'left' }}>
          {resource ? (
            <>
              {resource.type === 'Video' &&
              resource.allow_download === false &&
              resource.transcoding_status === 'complete' ? (
                <HlsVideoViewer
                  video_url={`https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/${resource.uri}`}
                  thumbnail_url={
                    resource.thumbnail_uri
                      ? `https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/${resource.thumbnail_uri}`
                      : undefined
                  }
                />
              ) : resource.type === 'PDF' && resource.allow_download === false ? (
                <PdfViewer
                  storage_bucket={GCP_RESOURCE_UPLOAD_BUCKET}
                  storage_file_path={resource.uri}
                />
              ) : (
                <img
                  src={`https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/${resource.thumbnail_uri}`}
                  alt="RESOURCE"
                  style={{
                    width: '100%',
                    borderRadius: 4,
                    maxHeight: 450,
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  draggable={false}
                />
              )}
              <CaptionText weight="Medium" sx={{ my: 2 }}>
                {resource.description}
              </CaptionText>
              <CaptionText weight="Medium">By {resource.source}</CaptionText>
            </>
          ) : (
            <PrimarySkeleton width={'100%'} height={450} variant="rounded" />
          )}
        </Grid>
        <Grid size={5}>
          {resource ? (
            <>
              {resource_categories.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    color: theme.palette.primary.main,
                    alignItems: 'center'
                  }}
                >
                  <CategoryOutlinedIcon />
                  <MinorHeading weight="Medium"> | </MinorHeading>
                  {resource_categories.map((category: string, i: number) => (
                    <>
                      <MinorHeading weight="Medium" key={category}>
                        {category}
                      </MinorHeading>
                      {i < resource_categories.length - 1 && (
                        <MinorHeading weight="Medium"> | </MinorHeading>
                      )}
                    </>
                  ))}
                </Box>
              )}
              <Heading weight="Medium" sx={{ textAlign: 'left', my: 6, wordBreak: 'break-word' }}>
                {resource.name}
              </Heading>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: theme.palette.primary.main
                }}
              >
                <BodyText>
                  <strong>Created:</strong> {moment(resource.created).format('DD MMM YYYY')}
                </BodyText>
                <BodyText>
                  <strong>Modified:</strong>{' '}
                  {moment(resource.modified ? resource.modified : resource.created).format(
                    'DD MMM YYYY'
                  )}
                </BodyText>
              </Box>
              {resource.allow_download !== false && (
                <BorderContainer
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    my: 3,
                    color: theme.palette.primary.main
                  }}
                >
                  <PrimaryButton
                    startIcon={<DownloadIcon height={25} stroke="currentColor" strokeWidth={2} />}
                    loading={loadingResourceDownload}
                    onClick={async () => await handleResourceDownload()}
                    sx={{ width: 175, height: 45 }}
                  >
                    Download
                  </PrimaryButton>
                  <BodyText weight="Medium">{`${resourceMetadata ? getDownloadsDisplay(resourceMetadata.download_count) : 0} Downloads`}</BodyText>
                  <BodyText>{resource.type}</BodyText>
                  <BodyText sx={{ color: theme.palette.primary.main }}>
                    {getFileSizeDisplay(resource.size)}
                  </BodyText>
                </BorderContainer>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {resource_tags.map((tag: string) => (
                  <ColorTag key={tag} color={theme.palette.teal.dark} text={tag} />
                ))}
              </Box>
            </>
          ) : (
            <PrimarySkeleton width={'100%'} height={450} variant="rounded" />
          )}
        </Grid>
      </Grid>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default ResourceCentreDetails;
